/**
 * Hook that resolves BLE-discovered ICCIDs to radio rows with site location
 * metadata in realtime, layering over the raw discoveredUnits scan map.
 *
 * Resolves newly-seen ICCIDs on a short cadence (~4s) and re-resolves the
 * full current set on a longer cadence (~30s) so stale records stay fresh.
 * Stops all timers on unmount. Web-safe — does nothing when there are no
 * discovered units.
 *
 * Contains:
 * - useNearbyUnitResolution — feature-scoped hook (default export)
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { resolveRadios } from '@sophocles/api/radios';

/** How often (ms) to attempt resolution of any newly-seen ICCIDs. */
const RESOLVE_NEW_INTERVAL_MS = 4_000;

/** How often (ms) to re-resolve the full current ICCID set. */
const RESOLVE_FULL_INTERVAL_MS = 30_000;

/**
 * Resolves BLE-discovered ICCIDs to radio rows with site location data in realtime.
 *
 * Newly-seen ICCIDs are resolved every ~4 s; the entire set is
 * re-resolved every ~30 s to keep data fresh as the scan map updates.
 *
 * @param {Map<string, { deviceId: string, rssi: number, name: string|null }>} discoveredUnits
 *   The live scan map from useBluetooth(). Keyed by full ICCID.
 * @returns {{
 *   resolvedMap: Map<string, {
 *     id: number,
 *     iccid: string,
 *     mac_address: string,
 *     location: {
 *       customer: { id: number, name: string },
 *       site_group: { id: number, name: string, description: string },
 *       site: { id: number, name: string, description: string }
 *     } | null,
 *     online?: boolean
 *   }>,
 *   isResolving: boolean
 * }}
 */
export default function useNearbyUnitResolution(discoveredUnits) {
  const [resolvedMap, setResolvedMap] = useState(() => new Map());
  const [isResolving, setIsResolving] = useState(false);

  // Latest-value refs so interval callbacks don't capture stale state.
  const resolvedMapRef = useRef(resolvedMap);
  resolvedMapRef.current = resolvedMap;

  const discoveredUnitsRef = useRef(discoveredUnits);
  discoveredUnitsRef.current = discoveredUnits;

  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const mergeResolved = useCallback(results => {
    if (!mountedRef.current) return;
    setResolvedMap(prev => {
      const next = new Map(prev);
      for (const entry of results) {
        next.set(entry.iccid, entry);
      }
      return next;
    });
  }, []);

  const resolveNew = useCallback(async () => {
    const current = discoveredUnitsRef.current;
    if (current.size === 0) return;

    const already = resolvedMapRef.current;
    const toResolve = Array.from(current.keys()).filter(iccid => !already.has(iccid));
    if (toResolve.length === 0) return;

    setIsResolving(true);
    try {
      const results = await resolveRadios(toResolve);
      if (mountedRef.current) mergeResolved(results);
    } catch {
      // Non-fatal — next interval will retry.
    } finally {
      if (mountedRef.current) setIsResolving(false);
    }
  }, [mergeResolved]);

  const resolveFull = useCallback(async () => {
    const current = discoveredUnitsRef.current;
    if (current.size === 0) return;

    const iccids = Array.from(current.keys());
    setIsResolving(true);
    try {
      const results = await resolveRadios(iccids);
      if (mountedRef.current) mergeResolved(results);
    } catch {
      // Non-fatal.
    } finally {
      if (mountedRef.current) setIsResolving(false);
    }
  }, [mergeResolved]);

  useEffect(() => {
    // Kick off an immediate new-ICCID resolve whenever the scan map grows.
    resolveNew();
  }, [discoveredUnits.size, resolveNew]);

  useEffect(() => {
    const newTimer = setInterval(resolveNew, RESOLVE_NEW_INTERVAL_MS);
    const fullTimer = setInterval(resolveFull, RESOLVE_FULL_INTERVAL_MS);
    return () => {
      clearInterval(newTimer);
      clearInterval(fullTimer);
    };
  }, [resolveNew, resolveFull]);

  return useMemo(() => ({ resolvedMap, isResolving }), [resolvedMap, isResolving]);
}
